import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'Sitaare - AI-Powered Vedic Astrology Readings';
const DEFAULT_DESCRIPTION = 'Get personalized Vedic astrology readings powered by AI. Analyze your birth chart, discover your nakshatra, and receive guidance on love, career, health, and life purpose.';
const DEFAULT_IMAGE = 'https://sitaare.ai/sitaare_og.jpg';
const SITE_URL = 'https://sitaare.ai';

// Must match the app's actual routes (App.js) — none of them have a trailing slash.
// A canonical/sitemap URL that doesn't match the real route causes Google to see
// every crawl as a redirect (GSC "Page with redirect"), since the served URL and
// the URL the page claims as canonical are two different paths.
const normalizePath = (path = '') => {
  if (!path || path === '/') return '/';
  return path.endsWith('/') ? path.slice(0, -1) : path;
};

const absoluteUrl = (path) => `${SITE_URL}${normalizePath(path)}`;

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  type = 'website',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title ? `${title} | Sitaare` : DEFAULT_TITLE;
  const canonicalUrl = absoluteUrl(path);
  const schemas = useMemo(() => {
    if (!jsonLd) return [];
    if (typeof document !== 'undefined' && document.querySelector('script[data-static-seo="true"]')) {
      return [];
    }
    return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  }, [jsonLd]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1512" />
      <meta property="og:image:height" content="756" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// Pre-defined JSON-LD schemas for different page types
export const createFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

export const createBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const createArticleSchema = ({ title, description, datePublished, dateModified }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  datePublished,
  dateModified: dateModified || datePublished,
  author: {
    '@type': 'Organization',
    name: 'Sitaare',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Sitaare',
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
    },
  },
});

export default SEO;
